import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth
export const adminLogin = (credentials) => api.post('/auth/login', credentials);
export const verifyAuth = () => api.get('/auth/verify');
export const forgotPassword = (email) => api.post('/auth/forgot-password', { email });
export const verifyResetToken = (token) => api.post('/auth/verify-reset-token', { token });
export const resetPassword = (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword });

// Ministries
export const getMinistries = () => api.get('/ministries');
export const getMinistry = (slug) => api.get(`/ministries/${slug}`);
export const createMinistry = (data) => api.post('/ministries', data);
export const updateMinistry = (id, data) => api.put(`/ministries/${id}`, data);
export const deleteMinistry = (id) => api.delete(`/ministries/${id}`);

// Sermons
export const getSermons = (params) => api.get('/sermons', { params });
export const getSermon = (slug) => api.get(`/sermons/${slug}`);
export const getFeaturedSermons = (limit = 3) => api.get('/sermons/featured', { params: { limit } });
export const getSermonFilters = () => api.get('/sermons/filters');
export const createSermon = (data) => api.post('/sermons', data);
export const updateSermon = (id, data) => api.put(`/sermons/${id}`, data);
export const deleteSermon = (id) => api.delete(`/sermons/${id}`);

// Events
export const getEvents = (params) => api.get('/events', { params });
export const getEvent = (slug) => api.get(`/events/${slug}`);
export const getEventsByMonth = () => api.get('/events/calendar/months');
export const getEventCategories = () => api.get('/events/categories');
export const getRelatedEvents = (slug, limit = 4) => api.get(`/events/${slug}/related`, { params: { limit } });
export const getEventNavigation = (slug) => api.get(`/events/${slug}/navigation`);
export const createEvent = (data) => api.post('/events', data);
export const updateEvent = (id, data) => api.put(`/events/${id}`, data);
export const deleteEvent = (id) => api.delete(`/events/${id}`);
export const submitEventRSVP = (data) => api.post('/forms/event-rsvp', data);
export const getEventRSVPs = (eventId) => api.get(`/events/${eventId}/rsvps`);

// Team Members
export const getTeamMembers = () => api.get('/team-members');
export const getTeamMember = (id) => api.get(`/team-members/${id}`);
export const createTeamMember = (data) => api.post('/team-members', data);
export const updateTeamMember = (id, data) => api.put(`/team-members/${id}`, data);
export const deleteTeamMember = (id) => api.delete(`/team-members/${id}`);

// Testimonials
export const getTestimonials = () => api.get('/testimonials');
export const getAllTestimonials = () => api.get('/admin/testimonials');
export const createTestimonial = (data) => api.post('/testimonials', data);
export const updateTestimonial = (id, data) => api.put(`/testimonials/${id}`, data);
export const deleteTestimonial = (id) => api.delete(`/testimonials/${id}`);
export const submitTestimonialForm = (data) => api.post('/forms/testimonial', data);

// People
export const getPeople = (params) => api.get('/people', { params });
export const getPerson = (id) => api.get(`/people/${id}`);

// Unified Forms
export const submitPlanVisitForm = (data) => api.post('/forms/plan-visit', data);
export const submitPrayerRequest = (data) => api.post('/forms/prayer-request', data);
export const submitSalvationForm = (data) => api.post('/forms/salvation', data);
export const submitBaptismForm = (data) => api.post('/forms/baptism', data);
export const submitMembershipForm = (data) => api.post('/forms/membership', data);
export const submitPartnershipForm = (data) => api.post('/forms/partnership', data);
export const submitNextStepClassForm = (data) => api.post('/forms/next-step-class', data);
export const submitVolunteerForm = (data) => api.post('/forms/volunteer', data);
export const submitContactForm = (data) => api.post('/forms/contact', data);

// Ministry Signup Forms
export const submitWorshipSignup = (data) => api.post('/submit/ministry/worship', data);
export const submitLifeGroupsSignup = (data) => api.post('/submit/ministry/lifegroups', data);
export const submitMissionsSignup = (data) => api.post('/submit/ministry/missions', data);
export const submitWomensMinistrySignup = (data) => api.post('/submit/ministry/womens', data);
export const submitKingdomKidsSignup = (data) => api.post('/submit/ministry/kingdom-kids', data);
export const submitKingdomStudentsSignup = (data) => api.post('/submit/ministry/kingdom-students', data);

// Unified Submissions
export const getSubmissions = (params) => api.get('/submissions', { params });
export const getSubmission = (id) => api.get(`/submissions/${id}`);
export const updateSubmission = (id, data) => api.put(`/submissions/${id}`, data);
export const getSubmissionStats = () => api.get('/submissions/stats');
export const exportSubmissionsCSV = (params) => 
  api.get('/submissions/export/csv', { params, responseType: 'blob' });

// Legacy Admin Submissions (backward compatibility)
export const getAdminSubmissions = (params) => api.get('/admin/submissions', { params });
export const updateSubmissionStatus = (id, data) => api.put(`/admin/submissions/${id}`, data);

// Notes
export const createNote = (data) => api.post('/notes', data);
export const getNotes = (submissionId) => api.get(`/notes/${submissionId}`);

// Next Step Classes
export const getNextStepClasses = () => api.get('/next-step-classes');

// Media Library
export const getMediaItems = (params) => api.get('/media', { params });
export const getMediaItem = (id) => api.get(`/media/${id}`);
export const createMediaItem = (data) => api.post('/media', data);
export const updateMediaItem = (id, data) => api.put(`/media/${id}`, data);
export const deleteMediaItem = (id) => api.delete(`/media/${id}`);
export const getMediaCount = () => api.get('/media/stats/count');
export const uploadMediaFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/media/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Settings
export const getSettings = () => api.get('/settings');
export const updateSettings = (data) => api.put('/settings', data);

// Staff Management
export const getStaffMembers = () => api.get('/admin/staff');
export const getStaffMember = (id) => api.get(`/admin/staff/${id}`);
export const createStaffMember = (data) => api.post('/admin/staff', data);
export const updateStaffMember = (id, data) => api.put(`/admin/staff/${id}`, data);
export const deleteStaffMember = (id) => api.delete(`/admin/staff/${id}`);
export const getStaffSubmissions = (staffId) => api.get(`/admin/staff/${staffId}/submissions`);

// Communication Tracking
export const getCommunications = (params) => api.get('/admin/communications', { params });
export const getSubmissionCommunications = (submissionId) => api.get(`/admin/communications/${submissionId}`);
export const createCommunication = (data, staffId, staffName) => 
  api.post(`/admin/communications?staff_id=${staffId}&staff_name=${encodeURIComponent(staffName)}`, data);
export const updateCommunication = (id, data) => api.put(`/admin/communications/${id}`, data);
export const markResponseReceived = (id, notes) => api.post(`/admin/communications/${id}/response-received`, { notes });

// Notifications
export const getNotificationHistory = (params) => api.get('/admin/notifications', { params });
export const sendTestNotification = (staffId) => api.post(`/admin/notifications/send-test?staff_id=${staffId}`);

// Admin Notification Logs (Form Submission Notifications)
export const getAdminNotificationLogs = (params) => api.get('/admin/notification-logs', { params });
export const getAdminNotificationStats = () => api.get('/admin/notification-logs/stats');

// Message Templates
export const getMessageTemplates = (submissionType) => api.get('/admin/templates', { params: submissionType ? { submission_type: submissionType } : {} });
export const getMessageTemplate = (id) => api.get(`/admin/templates/${id}`);
export const createMessageTemplate = (data) => api.post('/admin/templates', data);
export const updateMessageTemplate = (id, data) => api.put(`/admin/templates/${id}`, data);
export const deleteMessageTemplate = (id) => api.delete(`/admin/templates/${id}`);
export const testMessageTemplate = (id, testEmail, testPhone) => 
  api.post(`/admin/templates/${id}/test`, null, { params: { test_email: testEmail, test_phone: testPhone } });
export const getMessageLogs = (params) => api.get('/admin/message-logs', { params });

// Event Templates
export const getEventTemplates = () => api.get('/admin/event-templates');
export const createEventTemplate = (data) => api.post('/admin/event-templates', data);
export const updateEventTemplate = (id, data) => api.put(`/admin/event-templates/${id}`, data);
export const deleteEventTemplate = (id) => api.delete(`/admin/event-templates/${id}`);
export const createEventFromTemplate = (templateId, eventDate) => 
  api.post(`/admin/event-templates/${templateId}/create-event?event_date=${eventDate}`);

// Page Content Management
export const getAllPages = () => api.get('/pages');
export const getPageContent = (pageSlug) => api.get(`/pages/${pageSlug}`);
export const updatePageContent = (pageSlug, data) => api.put(`/pages/${pageSlug}`, data);
export const deletePageSection = (pageSlug, sectionId) => api.delete(`/pages/${pageSlug}/sections/${sectionId}`);

// Seed
export const seedDatabase = () => api.post('/seed');

// Global Search
export const globalSearch = (query) => api.get('/search', { params: { q: query } });

// ============== LIVE ENGAGEMENT SYSTEM ==============

// Live Chat
export const getLiveChatMessages = (limit = 50, since = null) => 
  api.get('/live/chat/messages', { params: { limit, since } });
export const postLiveChatMessage = (data) => api.post('/live/chat/messages', data);
export const postLiveReaction = (reactionType) => 
  api.post('/live/chat/reaction', null, { params: { reaction_type: reactionType } });
export const getLiveChatStatus = () => api.get('/live/chat/status');

// Live Guest Connect
export const submitLiveGuestConnect = (data) => api.post('/live/guest-connect', data);

// Live Prayer Requests
export const submitLivePrayerRequest = (data) => api.post('/live/prayer-request', data);
export const getPublicPrayerRequests = () => api.get('/live/prayer-requests/public');

// Live Salvation Decisions
export const submitLiveSalvationDecision = (data) => api.post('/live/salvation-decision', data);

// Live CTAs
export const getLiveCTAs = () => api.get('/live/ctas');

// Live Settings
export const getLiveSettings = () => api.get('/live/settings');

// Live Stats
export const getLiveStats = () => api.get('/live/stats');

// Admin - Live Chat
export const deleteLiveChatMessage = (messageId) => api.delete(`/live/admin/chat/messages/${messageId}`);
export const approveLiveChatMessage = (messageId) => api.put(`/live/admin/chat/messages/${messageId}/approve`);
export const muteUser = (ipAddress, minutes = 10) => 
  api.post(`/live/admin/chat/users/${encodeURIComponent(ipAddress)}/mute`, null, { params: { minutes } });
export const banUser = (ipAddress) => api.post(`/live/admin/chat/users/${encodeURIComponent(ipAddress)}/ban`);
export const unbanUser = (ipAddress) => api.post(`/live/admin/chat/users/${encodeURIComponent(ipAddress)}/unban`);
export const getLiveChatUsers = () => api.get('/live/admin/chat/users');
export const getPendingMessages = () => api.get('/live/admin/chat/messages/pending');
export const exportChatLog = () => api.get('/live/admin/chat/export');

// Admin - Live Guest Connects
export const getLiveGuestConnects = () => api.get('/live/admin/guest-connects');

// Admin - Live Prayer Requests
export const getLivePrayerRequests = () => api.get('/live/admin/prayer-requests');
export const approveLivePrayerRequest = (prayerId) => api.put(`/live/admin/prayer-requests/${prayerId}/approve`);

// Admin - Live Salvation Decisions
export const getLiveSalvationDecisions = () => api.get('/live/admin/salvation-decisions');

// Admin - Live CTAs
export const getAllLiveCTAs = () => api.get('/live/admin/ctas');
export const createLiveCTA = (data) => api.post('/live/admin/ctas', data);
export const updateLiveCTA = (ctaId, data) => api.put(`/live/admin/ctas/${ctaId}`, data);
export const deleteLiveCTA = (ctaId) => api.delete(`/live/admin/ctas/${ctaId}`);

// Admin - Live Settings
export const updateLiveSettings = (data) => api.put('/live/admin/settings', data);
export const updatePinnedMessage = (message) => api.put('/live/admin/settings/pinned-message', { message });
export const clearPinnedMessage = () => api.delete('/live/admin/settings/pinned-message');

// Admin - Live Schedules
export const getLiveSchedules = () => api.get('/live/admin/schedules');
export const createLiveSchedule = (data) => api.post('/live/admin/schedules', data);
export const updateLiveSchedule = (scheduleId, data) => api.put(`/live/admin/schedules/${scheduleId}`, data);
export const deleteLiveSchedule = (scheduleId) => api.delete(`/live/admin/schedules/${scheduleId}`);

// Admin - Chat History
export const getLiveChatHistory = (params) => api.get('/live/admin/chat-history', { params });
export const clearLiveChatHistory = (beforeDate = null) => 
  api.delete('/live/admin/chat-history/clear', { params: { before_date: beforeDate } });

// ============== ENHANCED LIVE ENGAGEMENT ==============

// Live Polls
export const createLivePoll = (data) => api.post('/live/enhanced/polls', data);
export const getActivePoll = () => api.get('/live/enhanced/polls/active');
export const voteOnPoll = (pollId, optionIndex) => api.post(`/live/enhanced/polls/${pollId}/vote`, { option_index: optionIndex });
export const getPollResults = (pollId) => api.get(`/live/enhanced/polls/${pollId}/results`);
export const closePoll = (pollId) => api.put(`/live/enhanced/polls/${pollId}/close`);
export const getPollHistory = (limit = 20) => api.get('/live/enhanced/polls/history', { params: { limit } });

// Prayer Wall
export const submitPrayerWallItem = (data) => api.post('/live/enhanced/prayer-wall', data);
export const getPrayerWall = (limit = 50) => api.get('/live/enhanced/prayer-wall', { params: { limit } });
export const prayForItem = (itemId) => api.post(`/live/enhanced/prayer-wall/${itemId}/pray`);
export const getPrayerWallStats = () => api.get('/live/enhanced/prayer-wall/stats');

// Reaction Aggregation
export const aggregateReaction = (reactionType) => api.post('/live/enhanced/reactions/aggregate', null, { params: { reaction_type: reactionType } });
export const getReactionAggregates = () => api.get('/live/enhanced/reactions/aggregate');
export const resetReactionAggregates = () => api.post('/live/enhanced/reactions/reset');

// Q&A Mode
export const submitQAQuestion = (data) => api.post('/live/enhanced/qa/questions', data);
export const getQAQuestions = (status = 'approved') => api.get('/live/enhanced/qa/questions', { params: { status } });
export const upvoteQuestion = (questionId) => api.post(`/live/enhanced/qa/questions/${questionId}/upvote`);
export const approveQuestion = (questionId) => api.put(`/live/enhanced/qa/questions/${questionId}/approve`);
export const markQuestionAnswered = (questionId) => api.put(`/live/enhanced/qa/questions/${questionId}/answer`);
export const getPendingQuestions = () => api.get('/live/enhanced/qa/questions/pending');

// Sermon Notes
export const createSermonNotes = (data) => api.post('/live/enhanced/sermon-notes', data);
export const getActiveSermonNotes = () => api.get('/live/enhanced/sermon-notes/active');
export const addSermonBookmark = (notesId, data) => api.post(`/live/enhanced/sermon-notes/${notesId}/bookmark`, data);
export const getMyBookmarks = () => api.get('/live/enhanced/sermon-notes/bookmarks');

// Slow Mode
export const getSlowMode = () => api.get('/live/enhanced/slow-mode');
export const setSlowMode = (data) => api.put('/live/enhanced/slow-mode', data);

// Viewer Badges
export const getViewerBadges = () => api.get('/live/enhanced/badges');
export const trackViewerView = () => api.post('/live/enhanced/badges/track-view');

// ============== PHASE 2 LIVE ENGAGEMENT ==============

// AI Moderation
export const moderateMessage = (message, name) => api.post('/live/phase2/moderate', null, { params: { message, name } });
export const getFlaggedMessages = (limit = 50) => api.get('/live/phase2/moderation/flagged', { params: { limit } });

// Digital Bulletin
export const createBulletin = (data) => api.post('/live/phase2/bulletin', data);
export const getActiveBulletin = () => api.get('/live/phase2/bulletin');

// Worship Lyrics
export const setWorshipLyrics = (data) => api.post('/live/phase2/lyrics', data);
export const getCurrentLyrics = () => api.get('/live/phase2/lyrics');
export const clearLyrics = () => api.delete('/live/phase2/lyrics');

// Scripture References
export const addScriptureReference = (data) => api.post('/live/phase2/scripture', data);
export const getActiveScriptures = () => api.get('/live/phase2/scripture/active');
export const parseScriptureReferences = (text) => api.get('/live/phase2/scripture/parse', { params: { text } });

// Life Group Seating
export const getAvailableLifeGroups = () => api.get('/live/phase2/life-groups');
export const joinLifeGroup = (data) => api.post('/live/phase2/life-groups/join', data);
export const getGroupViewers = (groupId) => api.get(`/live/phase2/life-groups/${groupId}/viewers`);

// Post-Service Lobby
export const openPostServiceLobby = (durationMinutes = 30) => api.post('/live/phase2/lobby/open', null, { params: { duration_minutes: durationMinutes } });
export const closeLobby = () => api.post('/live/phase2/lobby/close');
export const getLobbyStatus = () => api.get('/live/phase2/lobby/status');

// Prayer Partner Matching
export const requestPrayerPartner = (data) => api.post('/live/phase2/prayer-partner/request', data);
export const checkPrayerPartnerStatus = (requestId) => api.get(`/live/phase2/prayer-partner/status/${requestId}`);

// Private Reporting
export const submitPrivateReport = (data) => api.post('/live/phase2/report', data);
export const getReports = (status = 'pending') => api.get('/live/phase2/reports', { params: { status } });
export const reviewReport = (reportId, action) => api.put(`/live/phase2/reports/${reportId}/review`, null, { params: { action } });

// Analytics Dashboard
export const getEngagementDashboard = () => api.get('/live/phase2/analytics/dashboard');
export const getSentimentTracking = () => api.get('/live/phase2/analytics/sentiment');

// Follow-Up Intelligence
export const getFollowUpList = () => api.get('/live/phase2/follow-up/list');


// Admin - Prayer Team Dashboard
export const getPrayerRequests = () => api.get('/admin/prayer-team/requests');
export const updatePrayerRequestStatus = (requestId, status) => 
  api.put(`/admin/prayer-team/requests/${requestId}/status`, { status });
export const assignPrayerRequest = (requestId, staffId) => 
  api.put(`/admin/prayer-team/requests/${requestId}/assign`, { staff_id: staffId });

// Connect Card
export const submitConnectCard = (data) => api.post('/forms/connect-card', data);

export default api;
